import React, { useEffect, useState } from "react";
import { Container, Button, Icon, Modal, Loader } from "semantic-ui-react";
import { useParams, useHistory, useLocation, Link } from "react-router-dom";
import { size } from "lodash";
import { useTable, useSiteConfig } from "../../hooks";
import { ModalConfirm } from "../../components/Common";
import { getOrdersByTableApi } from "../../api/orders";
import { verifyTableAccessApi } from "../../api/table";
import {
  saveTableAccess,
  getTableAccessCode,
  clearTableAccess,
} from "../../api/tableAccess";
import {
  cleanProductCartApi,
  getProductsCart,
  CART_UPDATED_EVENT,
} from "../../api/cart";
import { isClockDebug } from "../../utils/serverClock";
import "./ClientLayout.scss";

export function ClientLayout(props) {
  const { children } = props;
  const { getTableByNumber } = useTable();
  const { siteConfig, getSiteConfig } = useSiteConfig();
  const { tableNumber } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [accessOk, setAccessOk] = useState(false);
  // "Terraza · Mesa 3" (o "Mesa 3" con un solo ambiente), lo devuelve la verificación.
  const [place, setPlace] = useState("");
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showExitBlocked, setShowExitBlocked] = useState(false);
  const [hasCartItems, setHasCartItems] = useState(
    () => getProductsCart().length > 0
  );

  // Acceso a la mesa: el código llega en el QR (`?k=`) o ya quedó guardado
  // al entrar antes. Sin código válido (mesa ajena escrita a mano, código
  // regenerado, mesa eliminada) vuelve al inicio, que pide número + código.
  useEffect(() => {
    (async () => {
      setAccessOk(false);
      const codeFromQr = new URLSearchParams(location.search).get("k");
      const code = codeFromQr || getTableAccessCode(tableNumber);

      let table = null;
      try {
        table = code ? await verifyTableAccessApi(tableNumber, code) : null;
      } catch (error) {
        // Sin conexión no se puede verificar: si hay código se deja pasar
        // (el backend igual lo exige en cada pedido).
        table = code ? { number: tableNumber } : null;
      }

      if (!table) {
        closeTable(
          code
            ? "El código de la mesa no es válido. Vuelve a escanear el QR de tu mesa."
            : "Escanea el código QR de tu mesa, o ingresa el código impreso debajo del QR."
        );
        return;
      }

      setPlace(table.place || "");
      saveTableAccess(tableNumber, code.toUpperCase());
      if (codeFromQr) history.replace(location.pathname); // saca ?k= de la barra
      setAccessOk(true);
    })();
  }, [tableNumber]);

  useEffect(() => {
    getSiteConfig();
  }, []);

  useEffect(() => {
    const updateCartState = () => setHasCartItems(getProductsCart().length > 0);
    updateCartState();
    window.addEventListener(CART_UPDATED_EVENT, updateCartState);
    return () => window.removeEventListener(CART_UPDATED_EVENT, updateCartState);
  }, [tableNumber]);

  const closeTable = (error) => {
    cleanProductCartApi();
    clearTableAccess();
    history.push("/", error ? { error, tableNumber } : undefined);
  };

  // "Salir" solo se permite si la mesa no tiene pedidos abiertos (ni
  // enviados a cocina ni servidos): una vez en cocina ya hay un compromiso
  // y hay que pedir la cuenta (decisión explícita, opción A). Desde el
  // celular salir nunca registra pérdida — eso lo decide el Trabajador con
  // "Liberar mesa" —, pero así el comensal no se "va" de la app debiendo.
  const onExitClick = async () => {
    try {
      const table = await getTableByNumber(tableNumber);
      const orders = await getOrdersByTableApi(table[0].id, "", "");
      if (Array.isArray(orders) && size(orders) > 0) {
        setShowExitBlocked(true);
        return;
      }
    } catch (error) {
      // Sin conexión no se puede verificar: se cae a la confirmación simple.
    }
    setShowExitConfirm(true);
  };

  const goToCart = () => {
    history.push(`/client/${tableNumber}/cart`);
  };

  const goToOrders = () => {
    history.push(`/client/${tableNumber}/orders`);
  };

  return (
    <div className="client-layout-bg">
      <Container className="client-layout">
        <div className="client-layout__header">
          <Link to={`/client/${tableNumber}`} className="client-layout__brand">
            {siteConfig?.logo ? (
              <>
                <img src={siteConfig.logo} alt="Logo" />
                {siteConfig?.site_name && (
                  <span className="client-layout__site-name">
                    {siteConfig.site_name}
                  </span>
                )}
              </>
            ) : (
              <h1>iCard</h1>
            )}
          </Link>
          <span className="client-layout__table-number">{place || `Mesa ${tableNumber}`}</span>

          <div className="client-layout__actions">
            <Button
              icon
              title="Mi carrito"
              onClick={goToCart}
              className={`client-layout__cart-button${
                hasCartItems ? " client-layout__cart-button--has-items" : ""
              }`}
            >
              <Icon name="shop" />
              <span className="client-layout__action-label">Carrito</span>
            </Button>
            <Button icon title="Solicitado/Cuenta" onClick={goToOrders}>
              <Icon name="list" />
              <span className="client-layout__action-label">Pedidos</span>
            </Button>
            {/* Pide confirmación: en el celular es fácil tocarlo por error
                (está pegado a "Pedidos"). La salida automática cuando la
                mesa deja de existir sigue llamando a closeTable directo. */}
            <Button
              icon
              title="Abandonar mesa"
              onClick={onExitClick}
            >
              <Icon name="sign-out" />
              <span className="client-layout__action-label">Salir</span>
            </Button>
          </div>
        </div>

        <div className="client-layout__content">
          {accessOk ? (
            children
          ) : (
            <Loader active inline="centered">
              Verificando mesa...
            </Loader>
          )}
        </div>

        {/* Versión (SHA del commit): para confirmar que un celular no está
            usando una copia vieja de iCard guardada en caché. Oculta para el
            comensal (pedido del usuario): solo con ?debug=1 en la URL. */}
        {isClockDebug() && (
          <p className="client-layout__version">
            v {process.env.REACT_APP_VERSION || "dev"}
          </p>
        )}

        <ModalConfirm
          title="¿Salir de la mesa?"
          content={
            hasCartItems
              ? "Los productos de tu carrito que aún no pediste se borrarán."
              : "Podrás volver cuando quieras ingresando otra vez el número de mesa."
          }
          show={showExitConfirm}
          onClose={() => setShowExitConfirm(false)}
          onCloseText="Quedarme"
          onConfirm={() => {
            setShowExitConfirm(false);
            closeTable();
          }}
          onConfirmText="Salir"
        />

        <Modal
          className="modal-confirm"
          open={showExitBlocked}
          size="mini"
          onClose={() => setShowExitBlocked(false)}
        >
          <Modal.Header>Aún no puedes salir</Modal.Header>
          <Modal.Content>
            Ya tienes pedidos enviados a cocina. Pide la cuenta y espera a que
            te cobren antes de salir. Si hubo un error en tu pedido, avisa al
            mesero.
          </Modal.Content>
          <Modal.Actions>
            <Button
              primary
              style={{ width: "100%" }}
              onClick={() => setShowExitBlocked(false)}
            >
              Regresar
            </Button>
          </Modal.Actions>
        </Modal>
      </Container>
    </div>
  );
}
